import React from 'react';

import {
	ColoredStatusSelect,
	EditableDateCell,
	EditableTextCell,
	IconButton,
	SalesRepSelect,
} from './components';
import {
	classNames,
	formatCurrency,
	formatDate,
	isPastDate,
	normalizeDomain,
	sanitizeExternalUrl,
	timeAgo,
} from './utils';

function getLanguageLabel(value) {
	if (value === 'spanish') return 'Spanish';

	const labelMap = {
		en: 'English',
		es: 'Spanish',
	};

	return labelMap[String(value || '').toLowerCase()] || value || '—';
}

export function buildInstitutionsColumns({
	savingCell,
	salesRepOptions,
	onSaveField,
	onOpenPaymentsModal,
	onOpenLogsModal,
}) {
	return [
		{
			accessorKey: 'id',
			id: 'id',
			header: 'ID',
			size: 70,
			minSize: 60,
			cell: (info) => (
				<div className='font-mono text-xs text-zinc-700'>
					{info.getValue() ?? '—'}
				</div>
			),
		},
		{
			accessorKey: 'code',
			id: 'code',
			header: 'CODE',
			size: 40,
			enableSorting: false,
			minSize: 40,
			cell: (info) => {
				const value = info.getValue();
				return (
					<div className='font-mono text-xs text-zinc-700 block truncate'>
						{String(value || '—')
							.replace(/\s+/g, '')
							.toUpperCase()}
					</div>
				);
			},
		},
		{
			accessorKey: 'name',
			id: 'name',
			header: 'NAME',
			size: 260,
			minSize: 40,
			meta: { canStick: true },
			cell: (info) => (
				<div className='font-medium text-xs text-zinc-900 block truncate'>
					{info.getValue() || '—'}
				</div>
			),
		},
		{
			accessorKey: 'domain',
			id: 'domain',
			header: 'DOMAIN',
			enableSorting: false,
			size: 260,
			minSize: 40,
			cell: (info) => {
				const raw = info.getValue();
				const href = sanitizeExternalUrl(raw);
				const host = normalizeDomain(raw);

				return (
					<a
						href={href || '#'}
						target='_blank'
						rel='noreferrer'
						className={classNames(
							'text-xs',
							raw
								? 'text-zinc-600 hover:text-zinc-900 hover:underline block truncate'
								: 'text-zinc-400',
						)}
						onClick={(event) => {
							if (!href) event.preventDefault();
						}}
					>
						{host}
					</a>
				);
			},
		},
		{
			id: 'status',
			header: 'STATE',
			enableSorting: false,
			size: 180,
			minSize: 150,
			cell: ({ row }) => {
				const original = row.original;
				const currentValue =
					original?.state_id != null ? String(original.state_id) : '';

				return (
					<ColoredStatusSelect
						value={currentValue}
						disabled={savingCell === `${original.id}:state_id`}
						onChange={(newValue) =>
							onSaveField(
								original.id,
								'state_id',
								newValue ? Number(newValue) : null,
								{ requireModal: true },
							)
						}
					/>
				);
			},
		},
		{
			accessorKey: 'payments_summary.total_paid',
			id: 'total_paid',
			header: 'TOTAL PAID',
			size: 140,
			minSize: 100,
			cell: ({ row }) => {
				const value = row.original?.payments_summary?.total_paid;
				if (value == null) return <span className='text-zinc-400'>—</span>;

				return (
					<div className='text-xs font-medium text-emerald-700 block truncate'>
						{formatCurrency(value)}
					</div>
				);
			},
		},
		{
			accessorKey: 'payments_summary.pending_amount',
			id: 'pending_amount',
			header: 'PENDING AMOUNT',
			size: 160,
			minSize: 100,
			cell: ({ row }) => {
				const value = row.original?.payments_summary?.pending_amount;
				if (value == null) return <span className='text-zinc-400'>—</span>;

				return (
					<div className='text-xs font-medium text-amber-700 block truncate'>
						{formatCurrency(value)}
					</div>
				);
			},
		},
		{
			accessorKey: 'contract.cost',
			id: 'contract_cost',
			header: 'COST',
			size: 130,
			minSize: 100,
			cell: ({ row }) => {
				const value = row.original?.contract?.cost;
				if (value == null) return <span className='text-zinc-400'>—</span>;

				return (
					<div className='text-xs font-medium text-zinc-900 block truncate'>
						{formatCurrency(value)}
					</div>
				);
			},
		},
		{
			accessorKey: 'payments_summary.next_payment_date',
			id: 'next_payment_date',
			header: 'NEXT PAYMENT DATE',
			size: 160,
			minSize: 100,
			cell: ({ row }) => {
				const value = row.original?.payments_summary?.next_payment_date;
				const overdue = isPastDate(value);

				if (!value) return <span className='text-zinc-400'>—</span>;

				return (
					<span
						className={classNames(
							'inline-flex text-xs font-semibold block truncate',
							overdue ? 'text-rose-700' : 'text-zinc-700',
						)}
					>
						{formatDate(value)}
					</span>
				);
			},
		},
		{
			accessorKey: 'contract.end_date',
			id: 'contract_end_date',
			header: 'CONTRACT END DATE',
			size: 100,
			minSize: 100,
			cell: ({ row }) => {
				const value = row.original?.contract?.end_date;
				const expired = isPastDate(value);

				if (!value) return <span className='text-zinc-400'>—</span>;

				return (
					<span
						className={classNames(
							'text-xs font-medium truncate block',
							expired ? 'text-rose-700' : 'text-zinc-700',
						)}
					>
						{formatDate(value)}
					</span>
				);
			},
		},
		{
			accessorKey: 'Sales_Representative',
			id: 'Sales_Representative',
			header: 'SALES REP',
			enableSorting: false,
			size: 190,
			minSize: 170,
			cell: ({ row }) => {
				const currentValue =
					row.original?.Sales_Representative != null
						? String(row.original.Sales_Representative)
						: '';

				return (
					<SalesRepSelect
						value={currentValue}
						options={salesRepOptions}
						disabled={savingCell === `${row.original.id}:Sales_Representative`}
						onChange={(value) =>
							onSaveField(
								row.original.id,
								'Sales_Representative',
								value || null,
							)
						}
					/>
				);
			},
		},
		{
			accessorKey: 'Contact_institution',
			id: 'Contact_institution',
			enableSorting: false,
			header: 'CONTACT CLIENT',
			size: 220,
			minSize: 40,
			cell: ({ row }) => (
				<EditableTextCell
					value={row.original?.Contact_institution}
					disabled={savingCell === `${row.original.id}:Contact_institution`}
					onSave={(value) =>
						onSaveField(row.original.id, 'Contact_institution', value)
					}
				/>
			),
		},
		{
			accessorKey: 'language',
			id: 'language',
			enableSorting: false,
			header: 'LANGUAGE',
			size: 120,
			minSize: 100,
			cell: ({ row }) => (
				<span className='inline-flex items-center rounded-full bg-zinc-100 px-2.5 py-1 text-xs font-medium text-zinc-700'>
					{getLanguageLabel(row.original?.language)}
				</span>
			),
		},
		{
			accessorKey: 'Date_demo',
			id: 'Date_demo',
			header: 'Expiration Date',
			size: 180,
			minSize: 160,
			cell: ({ row }) => (
				<EditableDateCell
					value={row.original?.Date_demo}
					disabled={savingCell === `${row.original.id}:Date_demo`}
					onSave={(value) =>
						onSaveField(row.original.id, 'Date_demo', value, {
							requireModal: true,
						})
					}
				/>
			),
		},
		{
			id: 'actions',
			header: '',
			enableSorting: false,
			size: 100,
			minSize: 90,
			cell: ({ row }) => (
				<div className='flex justify-end gap-1'>
					<IconButton
						title='Transactions'
						onClick={() => onOpenPaymentsModal(row.original)}
					>
						<svg
							stroke='currentColor'
							fill='currentColor'
							strokeWidth='0'
							viewBox='0 0 1024 1024'
							height='25'
							width='25'
							xmlns='http://www.w3.org/2000/svg'
						>
							<path d='M512 64C264.6 64 64 264.6 64 512s200.6 448 448 448 448-200.6 448-448S759.4 64 512 64zm0 820c-205.4 0-372-166.6-372-372s166.6-372 372-372 372 166.6 372 372-166.6 372-372 372zm47.7-395.2l-25.4-5.9V348.6c38 5.2 61.5 29 65.5 58.2.5 4 3.9 6.9 7.9 6.9h44.9c4.7 0 8.4-4.1 8-8.8-6.1-62.3-57.4-102.3-125.9-109.2V263c0-4.4-3.6-8-8-8h-28.1c-4.4 0-8 3.6-8 8v33c-70.8 6.9-126.2 46-126.2 119 0 67.6 49.8 100.2 102.1 112.7l24.7 6.3v142.7c-44.2-5.9-69-29.5-74.1-61.3-.6-3.8-4-6.6-7.9-6.6H363c-4.7 0-8.4 4-8 8.7 4.5 55 46.2 105.6 135.2 112.1V761c0 4.4 3.6 8 8 8h28.4c4.4 0 8-3.6 8-8.1l-.2-31.7c78.3-6.9 134.3-48.8 134.3-124-.1-69.4-44.2-100.4-109-116.4zm-68.6-16.2c-5.6-1.6-10.3-3.1-15-5-33.8-12.2-49.5-31.9-49.5-57.3 0-36.3 27.5-57 64.5-61.7v124zM534.3 677V543.3c3.1.9 5.9 1.6 8.8 2.2 47.3 14.4 63.2 34.4 63.2 65.1 0 39.1-29.4 62.6-72 66.4z' />
						</svg>
					</IconButton>

					<IconButton
						title='History'
						onClick={() => onOpenLogsModal(row.original)}
					>
						<svg width='20' height='20' viewBox='0 0 20 20' fill='currentColor'>
							<path
								fillRule='evenodd'
								d='M10 3a7 7 0 106.93 8H15a1 1 0 110-2h4a1 1 0 011 1v4a1 1 0 11-2 0v-1.07A9 9 0 1110 1a1 1 0 110 2zm-.75 3a1 1 0 012 0v3.586l2.207 2.207a1 1 0 01-1.414 1.414l-2.5-2.5A1 1 0 019.25 10V6z'
								clipRule='evenodd'
							/>
						</svg>
					</IconButton>
				</div>
			),
		},
	];
}
